import { strToU8, zipSync } from 'fflate';
import type { ReportDataWarning, ReportGeneration } from './generated-report';

export type PecOperationalRowKey = 'incoming' | 'closed' | 'agency-backlog' | 'working';

export type PecOperationalRow = {
  key: PecOperationalRowKey;
  label: string;
  value: number;
};

export type PecTerritorialAreaId = 'nord-est' | 'centro' | 'nord-ovest' | 'sud';

export type PecTerritorialRow = {
  key: PecTerritorialAreaId;
  label: string;
  value: number;
};

export type PecTerritorialIssue = {
  city: string;
  province: string | null;
  count: number;
  reason: 'location-unresolved' | 'region-unassigned';
};

export type PecEntryChannelKey = 'web' | 'facebook' | 'linkedin' | 'x';

export type PecEntryChannelRow = {
  key: PecEntryChannelKey;
  label: string;
  closed: number;
  working: number;
  total: number;
};

export type PecTopicKey = 'general-information' | 'problems' | 'infratel-information' | 'complaints';

export type PecTopicRow = {
  key: PecTopicKey;
  label: string;
  value: number;
};

export type PecSocialReasonKey =
  | 'delivery'
  | 'coverage'
  | 'assurance'
  | 'other-general-information'
  | 'other-problems'
  | 'saleability'
  | 'creation'
  | 'operators';

export type PecSocialReasonRow = {
  key: PecSocialReasonKey;
  label: string;
  value: number;
};

export type PecAnnualSocialReasonKey = 'other' | 'coverage' | 'delivery' | 'assurance' | 'saleability';

export type PecAnnualSocialReasonRow = {
  key: PecAnnualSocialReasonKey;
  label: string;
  value: number;
};

export type PecSocialSubreasonAnalysis = {
  selectedYear: number;
  selectedMonth: number;
  periodLabel: string;
  totalIncoming: number;
  pecCases: number;
  closedCases: number;
  rows: PecOperationalRow[];
  territorialRows: PecTerritorialRow[];
  territorialResolvedCases: number;
  territorialIssues: PecTerritorialIssue[];
  channelCutoffDay: number;
  channelRows: PecEntryChannelRow[];
  channelTotals: {
    closed: number;
    working: number;
    total: number;
  };
  topicRows: PecTopicRow[];
  topicTotal: number;
  socialReasonRows: PecSocialReasonRow[];
  socialReasonTotal: number;
  annualSocialReasonRows: PecAnnualSocialReasonRow[];
  annualSocialReasonTotal: number;
  annualSocialReasonStartDate: string;
  annualSocialReasonEndDate: string;
  annualSocialReasonPeriodLabel: string;
  annualMasterFiles: Array<{
    id: string;
    name: string;
    path: string;
    modifiedTime: string | null;
    webViewLink: string | null;
  }>;
  masterFile: {
    id: string;
    name: string;
    path: string;
    modifiedTime: string | null;
    webViewLink: string | null;
  };
  mailFile: {
    id: string;
    name: string;
    path: string;
    modifiedTime: string | null;
    webViewLink: string | null;
    referenceDate: string | null;
  };
  worksheetName: string;
  sourceCoverage?: {
    masterPeriodRows: number;
    mailPeriodRows: number;
    annualMasterRows: number;
  };
  dataWarnings?: ReportDataWarning[];
  generation?: ReportGeneration;
};

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function inlineCell(reference: string, value: string, style: number): string {
  return `<c r="${reference}" t="inlineStr" s="${style}"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function numberCell(reference: string, value: number, style = 3): string {
  return `<c r="${reference}" s="${style}"><v>${Number.isFinite(value) ? value : 0}</v></c>`;
}

function workbookFiles(analysis: PecSocialSubreasonAnalysis): Record<string, Uint8Array> {
  const dataRows = analysis.rows.map((row, index) => {
    const excelRow = index + 3;
    return `<row r="${excelRow}" ht="24" customHeight="1">${[
      inlineCell(`A${excelRow}`, row.label, 2),
      numberCell(`B${excelRow}`, row.value),
    ].join('')}</row>`;
  }).join('');
  const totalRow = analysis.rows.length + 3;
  const territorialHeaderRow = totalRow + 2;
  const territorialSectionRow = territorialHeaderRow + 1;
  const territorialRows = analysis.territorialRows.map((row, index) => {
    const excelRow = territorialSectionRow + index + 1;
    return `<row r="${excelRow}" ht="24" customHeight="1">${[
      inlineCell(`A${excelRow}`, row.label, 2),
      numberCell(`B${excelRow}`, row.value),
    ].join('')}</row>`;
  }).join('');
  const territorialTotalRow = territorialSectionRow + analysis.territorialRows.length + 1;
  const channelHeaderRow = territorialTotalRow + 2;
  const channelColumnsRow = channelHeaderRow + 1;
  const channelRows = analysis.channelRows.map((row, index) => {
    const excelRow = channelColumnsRow + index + 1;
    return `<row r="${excelRow}" ht="24" customHeight="1">${[
      inlineCell(`A${excelRow}`, row.label, 2),
      numberCell(`B${excelRow}`, row.closed),
      numberCell(`C${excelRow}`, row.working),
      numberCell(`D${excelRow}`, row.total),
    ].join('')}</row>`;
  }).join('');
  const channelTotalRow = channelColumnsRow + analysis.channelRows.length + 1;
  const topicHeaderRow = channelTotalRow + 2;
  const topicSectionRow = topicHeaderRow + 1;
  const topicRows = analysis.topicRows.map((row, index) => {
    const excelRow = topicSectionRow + index + 1;
    return `<row r="${excelRow}" ht="24" customHeight="1">${[
      inlineCell(`A${excelRow}`, row.label, 2),
      numberCell(`B${excelRow}`, row.value),
    ].join('')}</row>`;
  }).join('');
  const topicLastRow = topicSectionRow + analysis.topicRows.length;
  const socialReasonHeaderRow = topicLastRow + 2;
  const socialReasonSectionRow = socialReasonHeaderRow + 1;
  const socialReasonRows = analysis.socialReasonRows.map((row, index) => {
    const excelRow = socialReasonSectionRow + index + 1;
    return `<row r="${excelRow}" ht="24" customHeight="1">${[
      inlineCell(`A${excelRow}`, row.label, 2),
      numberCell(`B${excelRow}`, row.value),
    ].join('')}</row>`;
  }).join('');
  const socialReasonLastRow = socialReasonSectionRow + analysis.socialReasonRows.length;
  const annualSocialReasonHeaderRow = socialReasonLastRow + 2;
  const annualSocialReasonSectionRow = annualSocialReasonHeaderRow + 1;
  const annualSocialReasonRows = analysis.annualSocialReasonRows.map((row, index) => {
    const excelRow = annualSocialReasonSectionRow + index + 1;
    return `<row r="${excelRow}" ht="24" customHeight="1">${[
      inlineCell(`A${excelRow}`, row.label, 2),
      numberCell(`B${excelRow}`, row.value),
    ].join('')}</row>`;
  }).join('');
  const annualSocialReasonLastRow = annualSocialReasonSectionRow + analysis.annualSocialReasonRows.length;
  const now = new Date().toISOString();

  return {
    '[Content_Types].xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`),
    '_rels/.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`),
    'docProps/core.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>Calcolo social subreason PEC ${escapeXml(analysis.periodLabel)}</dc:title>
  <dc:creator>Talos</dc:creator>
  <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created>
  <dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`),
    'docProps/app.xml': strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"><Application>Talos</Application></Properties>'),
    'xl/workbook.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <bookViews><workbookView xWindow="0" yWindow="0" windowWidth="18000" windowHeight="11000"/></bookViews>
  <sheets><sheet name="Report PEC" sheetId="1" r:id="rId1"/></sheets>
  <calcPr calcId="191029" fullCalcOnLoad="1"/>
</workbook>`),
    'xl/_rels/workbook.xml.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    'xl/styles.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="3">
    <font><sz val="11"/><color rgb="FF263C4D"/><name val="Aptos"/><family val="2"/></font>
    <font><b/><sz val="11"/><color rgb="FF172F43"/><name val="Aptos Display"/><family val="2"/></font>
    <font><b/><sz val="11"/><color rgb="FF172F43"/><name val="Aptos"/><family val="2"/></font>
  </fonts>
  <fills count="5">
    <fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFFFF54"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFE8B1EA"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF4F7F9"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border><border><left style="thin"/><right style="thin"/><top style="thin"/><bottom style="thin"/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="6">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="2" fillId="3" borderId="1" xfId="0" applyAlignment="1"><alignment vertical="center"/></xf>
    <xf numFmtId="3" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="4" borderId="1" xfId="0"/>
    <xf numFmtId="3" fontId="2" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`),
    'xl/worksheets/sheet1.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:D${annualSocialReasonLastRow}"/>
  <sheetViews><sheetView workbookViewId="0"><pane ySplit="2" topLeftCell="A3" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <sheetFormatPr defaultRowHeight="20"/>
  <cols><col min="1" max="1" width="28" customWidth="1"/><col min="2" max="2" width="35" customWidth="1"/><col min="3" max="4" width="22" customWidth="1"/></cols>
  <sheetData>
    <row r="1" ht="23" customHeight="1">${inlineCell('A1', 'Periodo', 4)}${inlineCell('B1', analysis.periodLabel, 5)}</row>
    <row r="2" ht="40" customHeight="1">${inlineCell('A2', 'MENSILE', 1)}${inlineCell('B2', 'SLIDE 4 (STATO OPERATIVO CASE)', 1)}</row>
    ${dataRows}
    <row r="${totalRow}" ht="24" customHeight="1">${inlineCell(`A${totalRow}`, 'Totale casi in entrata', 4)}${numberCell(`B${totalRow}`, analysis.totalIncoming, 5)}</row>
    <row r="${territorialHeaderRow}" ht="40" customHeight="1">${inlineCell(`A${territorialHeaderRow}`, 'MENSILE', 1)}${inlineCell(`B${territorialHeaderRow}`, 'SLIDE 5 (DISTRIBUZIONE CASE PER TIPOLOGIA)', 1)}</row>
    <row r="${territorialSectionRow}" ht="24" customHeight="1">${inlineCell(`A${territorialSectionRow}`, 'Macroargomenti', 2)}${inlineCell(`B${territorialSectionRow}`, '', 2)}</row>
    ${territorialRows}
    <row r="${territorialTotalRow}" ht="24" customHeight="1">${inlineCell(`A${territorialTotalRow}`, 'Totale PEC distribuite', 4)}${numberCell(`B${territorialTotalRow}`, analysis.territorialResolvedCases, 5)}</row>
    <row r="${channelHeaderRow}" ht="40" customHeight="1">${inlineCell(`A${channelHeaderRow}`, 'MENSILE', 1)}${inlineCell(`B${channelHeaderRow}`, 'SLIDE 6 (CANALI DI INGRESSO CASE)', 1)}${inlineCell(`C${channelHeaderRow}`, '', 1)}${inlineCell(`D${channelHeaderRow}`, '', 1)}</row>
    <row r="${channelColumnsRow}" ht="28" customHeight="1">${inlineCell(`A${channelColumnsRow}`, '', 2)}${inlineCell(`B${channelColumnsRow}`, 'CASI CHIUSI', 2)}${inlineCell(`C${channelColumnsRow}`, 'IN LAVORAZIONE', 2)}${inlineCell(`D${channelColumnsRow}`, 'TOTALE', 2)}</row>
    ${channelRows}
    <row r="${channelTotalRow}" ht="24" customHeight="1">${inlineCell(`A${channelTotalRow}`, 'Totale', 4)}${numberCell(`B${channelTotalRow}`, analysis.channelTotals.closed, 5)}${numberCell(`C${channelTotalRow}`, analysis.channelTotals.working, 5)}${numberCell(`D${channelTotalRow}`, analysis.channelTotals.total, 5)}</row>
    <row r="${topicHeaderRow}" ht="40" customHeight="1">${inlineCell(`A${topicHeaderRow}`, 'MENSILE', 1)}${inlineCell(`B${topicHeaderRow}`, 'SLIDE 5 (DISTRIBUZIONE CASE PER TIPOLOGIA)', 1)}</row>
    <row r="${topicSectionRow}" ht="24" customHeight="1">${inlineCell(`A${topicSectionRow}`, 'Macroargomenti', 2)}${inlineCell(`B${topicSectionRow}`, '', 2)}</row>
    ${topicRows}
    <row r="${socialReasonHeaderRow}" ht="40" customHeight="1">${inlineCell(`A${socialReasonHeaderRow}`, 'MENSILE', 1)}${inlineCell(`B${socialReasonHeaderRow}`, 'SLIDE 5 (DISTRIBUZIONE CASE PER TIPOLOGIA)', 1)}</row>
    <row r="${socialReasonSectionRow}" ht="24" customHeight="1">${inlineCell(`A${socialReasonSectionRow}`, 'Argomenti', 2)}${inlineCell(`B${socialReasonSectionRow}`, 'Richieste', 2)}</row>
    ${socialReasonRows}
    <row r="${annualSocialReasonHeaderRow}" ht="40" customHeight="1">${inlineCell(`A${annualSocialReasonHeaderRow}`, 'ANNUALE', 1)}${inlineCell(`B${annualSocialReasonHeaderRow}`, 'SLIDE 8 (DISTRIBUZIONE CASE PER TIPOLOGIA)', 1)}</row>
    <row r="${annualSocialReasonSectionRow}" ht="24" customHeight="1">${inlineCell(`A${annualSocialReasonSectionRow}`, 'Social Reason', 2)}${inlineCell(`B${annualSocialReasonSectionRow}`, 'Totale', 2)}</row>
    ${annualSocialReasonRows}
  </sheetData>
  <mergeCells count="1"><mergeCell ref="B${channelHeaderRow}:D${channelHeaderRow}"/></mergeCells>
  <pageMargins left="0.3" right="0.3" top="0.5" bottom="0.5" header="0.2" footer="0.2"/>
</worksheet>`),
  };
}

export function createPecSocialSubreasonWorkbook(analysis: PecSocialSubreasonAnalysis): Uint8Array {
  return zipSync(workbookFiles(analysis), { level: 6 });
}

export function pecSocialSubreasonWorkbookFileName(year: number, month: number): string {
  return `calcolo-social-subreason-pec-${year}-${String(month).padStart(2, '0')}.xlsx`;
}
